/**
 * KPI card with comparison, sparkline and coverage disclosure.
 *
 * The coverage footnote is not decoration. Discovery found 34.7% client-bridge
 * coverage and 76.2% missing completion times, so a bare number invites an
 * argument the dashboard cannot win. Showing coverage ends the argument.
 */
import { ArrowDownRight, ArrowRight, ArrowUpRight, Info } from "lucide-react";

import { delta, trendClass } from "@/utils/format";

export interface KpiCardProps {
  label: string;
  value: string;
  previous?: string;
  deltaPct?: number | null;
  higherIsBetter?: boolean;
  sparkline?: number[];
  /** Percentage of underlying rows this figure could actually be computed from. */
  coveragePct?: number | null;
  footnote?: string;
  onClick?: () => void;
  loading?: boolean;
}

export function KpiCard({
  label, value, previous, deltaPct, higherIsBetter = true,
  sparkline, coveragePct, footnote, onClick, loading,
}: KpiCardProps) {
  if (loading) {
    return (
      <div className="card p-4 space-y-3">
        <div className="skeleton h-3 w-24" />
        <div className="skeleton h-8 w-32" />
        <div className="skeleton h-3 w-20" />
      </div>
    );
  }

  const Icon = deltaPct == null || Math.abs(deltaPct) <= 3
    ? ArrowRight
    : deltaPct > 0 ? ArrowUpRight : ArrowDownRight;

  const interactive = Boolean(onClick);

  return (
    <div
      className={`card p-4 flex flex-col gap-2 ${
        interactive ? "cursor-pointer hover:border-rule-strong transition-colors" : ""
      }`}
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === "Enter") onClick?.(); } : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="label">{label}</span>
        {coveragePct != null && coveragePct < 95 && (
          <span
            className="chip border-warn/30 bg-warn-soft text-warn shrink-0"
            title={`Computed from ${coveragePct.toFixed(0)}% of underlying rows`}
          >
            <Info size={9} /> {coveragePct.toFixed(0)}%
          </span>
        )}
      </div>

      <div className="font-display font-bold text-[28px] leading-none tracking-tight tnum">
        {value}
      </div>

      <div className="flex items-center gap-2 text-[12.5px]">
        <span className={`inline-flex items-center gap-0.5 font-medium tnum ${trendClass(deltaPct, higherIsBetter)}`}>
          <Icon size={13} />
          {delta(deltaPct)}
        </span>
        {previous && <span className="text-ink-faint tnum">from {previous}</span>}
      </div>

      {sparkline && sparkline.length > 1 && <Sparkline values={sparkline} positive={higherIsBetter} />}
      {footnote && <p className="text-[11.5px] text-ink-faint leading-snug">{footnote}</p>}
    </div>
  );
}

function Sparkline({ values, positive }: { values: number[]; positive: boolean }) {
  const width = 120;
  const height = 26;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = width / (values.length - 1);
  const points = values.map((v, i) => [i * step, height - ((v - min) / span) * height] as const);
  const line = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  const area = `${line} L${width} ${height} L0 ${height} Z`;
  const last = points[points.length - 1]!;
  const rising = values[values.length - 1]! >= values[0]!;
  const stroke = rising === positive ? "#2F6B3C" : "#9E3B2E";

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-[26px]" aria-hidden="true">
      <path d={area} fill={stroke} opacity={0.08} />
      <path d={line} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinejoin="round" />
      {/* Emphasised endpoint: the reader's eye should land on "now". */}
      <circle cx={last[0]} cy={last[1]} r={2.2} fill={stroke} />
    </svg>
  );
}
